import React from 'react';
import LegalPage from '@/components/LegalPage';
import usePageTitle from '@/hooks/usePageTitle';

const PaymentTerms = () => {
  usePageTitle('Payment Terms');
  return (
  <LegalPage
    kicker="Legal"
    title="Payment Terms"
    lastUpdated="02 July 2026"
  >
    <p>
      These Payment Terms govern all financial transactions on{' '}
      <strong>adauraawards.com</strong>, including entry fees, sponsorship
      contributions and any other paid services. By making a payment you accept
      these terms in addition to our{' '}
      <a href="/terms">Terms &amp; Conditions</a>,{' '}
      <a href="/refund">Refund &amp; Cancellation Policy</a> and{' '}
      <a href="/privacy">Privacy Policy</a>.
    </p>

    <h2>1. Fees</h2>
    <ul>
      <li>The nomination fee for the 2026 edition is <strong>₹7,500 per entry</strong>, exclusive of GST.</li>
      <li><strong>18% GST</strong> (CGST + SGST for intra-state, IGST for inter-state) is added at checkout. All-inclusive per entry: <strong>₹8,850</strong>.</li>
      <li>Individual categories may carry an override price; the applicable fee is always displayed on the entry form before you confirm payment.</li>
      <li>Fees quoted on the site are in Indian Rupees (INR) only. International cards are converted by the issuer at their prevailing rate.</li>
    </ul>

    <h2>2. Payment gateway</h2>
    <ul>
      <li>All online payments are processed by <strong>Razorpay Software Private Limited</strong>, a PCI-DSS Level-1 certified payment aggregator regulated by the RBI.</li>
      <li>Marketing Mind does <strong>not</strong> collect, view or store your card number, CVV, UPI PIN, net-banking password, or any full banking credential. We only receive the Razorpay transaction ID and a masked instrument label (e.g., <em>“Visa ending 1234”</em>).</li>
      <li>Supported instruments include Indian credit &amp; debit cards, UPI, net-banking, popular wallets, and select international cards where enabled.</li>
    </ul>

    <h2>3. Invoicing &amp; GST</h2>
    <ul>
      <li>A GST-compliant tax invoice is auto-generated within minutes of successful payment and emailed to the entrant. It is also downloadable from the nominator portal under <strong>My Entries &rarr; Invoice</strong>.</li>
      <li>Invoices are issued by <strong>Digi Giant LLP</strong> (the legal entity that operates Marketing Mind and receives entry fees), with the entity’s GSTIN, PAN and registered address printed on the document.</li>
      <li>To claim input tax credit (ITC), please ensure your billing GSTIN is entered correctly during checkout. Corrections after 7 days require a fresh credit note and revised invoice cycle.</li>
    </ul>

    <h2>4. Confirmation &amp; failure</h2>
    <ul>
      <li>An entry is confirmed only when Razorpay returns a successful <em>captured</em> status and our server records the payment. Until then the entry sits in a <strong>Payment Pending</strong> state and does not enter the jury pool.</li>
      <li>If your account is debited but you did not receive a confirmation within 30 minutes, do not re-attempt the payment. Write to <a href="mailto:atuldwivedi@marketingmind.in">atuldwivedi@marketingmind.in</a> with the entry ID and the payment reference; we will reconcile with Razorpay and confirm the entry or refund the debit within 5 business days.</li>
      <li>Duplicate charges are refunded automatically within 5 business days — no request needed.</li>
    </ul>

    <h2>5. Discounts, coupons and waivers</h2>
    <p>
      Where a coupon code, early-bird slab or press waiver applies, it is
      applied at checkout before payment. Marketing Mind reserves the right to
      withdraw or modify promotional pricing at any time; entries paid at a
      published rate are honoured at that rate.
    </p>

    <h2>6. Chargebacks</h2>
    <p>
      If you dispute a charge with your card issuer, please contact us first —
      most reconciliations are resolvable directly and faster than a chargeback
      cycle. Successful chargebacks after entry submission may lead to the
      corresponding nomination being withdrawn from the jury pool without a
      further refund.
    </p>

    <h2>7. Currency &amp; taxes for non-Indian entrants</h2>
    <p>
      Non-Indian entities are billed in INR at 18% IGST unless they present a
      valid Indian GSTIN. Any withholding tax (TDS) applicable in the entrant’s
      country is the entrant’s responsibility; Marketing Mind cannot bear or
      refund such deductions.
    </p>

    <h2>8. Changes</h2>
    <p>
      Marketing Mind may revise entry fees for future editions with notice on
      the site. Fees for an edition, once published, will not be revised
      mid-cycle for entries already paid.
    </p>

    <h2>9. Support</h2>
    <p>
      Billing and payment queries — write to{' '}
      <a href="mailto:atuldwivedi@marketingmind.in">atuldwivedi@marketingmind.in</a>{' '}
      with your entry ID and the Razorpay payment reference. We respond within
      two business days.
    </p>
  </LegalPage>
  );
};

export default PaymentTerms;
